import { v4 as uuidv4 } from 'uuid';

import type {
  ChecklistStructuralFieldMutation,
  ChecklistStructuralMutationResult,
} from '../../adapters/IDatabaseAdapter';
import { APP_CONSTANTS } from '../../config/constants';
import { globalState } from '../../state/globalState';
import { databaseService } from '../databaseService';
import {
  assertStructuralOnline,
  type StructuralAction,
  type StructuralEntity,
  type StructuralGateResult,
} from './ChecklistStructuralOfflineGate';

export interface ChecklistStructuralMutationDatabase {
  createItem<T>(collection: string, item: T, skipPendingOperation?: boolean): Promise<string>;
  readItem<T>(collection: string, id: string): Promise<T | null>;
  listItems<T>(collection: string, query?: Record<string, unknown>): Promise<T[]>;
  updateItem<T>(collection: string, id: string, updates: Partial<T>, skipPendingOperation?: boolean): Promise<boolean>;
  retireItemAndPending(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    pendingData: Record<string, unknown>
  ): Promise<boolean>;
  applyChecklistStructuralMutation(input: {
    templateId: string;
    fieldMutations: ChecklistStructuralFieldMutation[];
  }): Promise<ChecklistStructuralMutationResult>;
}

export interface ChecklistStructuralMutationServiceOptions {
  database?: ChecklistStructuralMutationDatabase;
  isOnline?: () => boolean;
  now?: () => Date;
  createId?: () => string;
}

export class ChecklistStructuralMutationBlockedError extends Error {
  readonly gate: Extract<StructuralGateResult, { allowed: false }>;

  constructor(gate: Extract<StructuralGateResult, { allowed: false }>) {
    super(gate.message);
    this.name = 'ChecklistStructuralMutationBlockedError';
    this.gate = gate;
  }
}

type StructuralFieldRecord = Record<string, unknown> & {
  id?: string;
  template_id?: string;
};

type FieldUpdate = {
  id: string;
  updates: Record<string, unknown>;
};

const tables = APP_CONSTANTS.DATABASE.TABLES;

/**
 * Centraliza as mutações estruturais e mantém a fila remota coerente com o domínio.
 */
export class ChecklistStructuralMutationService {
  private readonly database: ChecklistStructuralMutationDatabase;
  private readonly isOnline: () => boolean;
  private readonly now: () => Date;
  private readonly createId: () => string;

  constructor(options: ChecklistStructuralMutationServiceOptions = {}) {
    this.database = options.database ?? databaseService;
    this.isOnline = options.isOnline ?? (() => globalState.network.isConnected.get());
    this.now = options.now ?? (() => new Date());
    this.createId = options.createId ?? uuidv4;
  }

  async createTemplate(input: {
    id?: string;
    empresaId: string;
    nome: string;
    versao?: number;
  }): Promise<string> {
    this.assertAllowed('template', 'create');
    const id = input.id ?? this.createId();
    const now = this.now().toISOString();
    await this.database.createItem(tables.TEMPLATES_CHECKLIST.NAME, {
      id,
      empresa_id: input.empresaId,
      nome: input.nome,
      versao: input.versao ?? 1,
      created_at: now,
      updated_at: now,
    });
    return id;
  }

  async renameTemplate(templateId: string, nome: string): Promise<boolean> {
    this.assertAllowed('template', 'edit');
    return this.database.updateItem(
      tables.TEMPLATES_CHECKLIST.NAME,
      templateId,
      { nome } as Record<string, unknown>
    );
  }

  async createFields(templateId: string, fields: StructuralFieldRecord[]): Promise<string[]> {
    this.assertAllowed('field', 'create');
    if (fields.length === 0) return [];

    const ids: string[] = [];
    const fieldMutations: ChecklistStructuralFieldMutation[] = fields.map(field => {
      const id = field.id ?? this.createId();
      ids.push(id);
      return {
        operation: 'create',
        record: {
          ...field,
          id,
          template_id: field.template_id ?? templateId,
        },
      };
    });
    await this.database.applyChecklistStructuralMutation({ templateId, fieldMutations });
    return ids;
  }

  async updateField(templateId: string, fieldId: string, updates: Record<string, unknown>): Promise<boolean> {
    const result = await this.updateFields(templateId, [{ id: fieldId, updates }]);
    return result[0] ?? false;
  }

  async updateFields(templateId: string, updates: FieldUpdate[]): Promise<boolean[]> {
    this.assertAllowed('field', 'edit');
    if (updates.length === 0) return [];

    const result = await this.database.applyChecklistStructuralMutation({
      templateId,
      fieldMutations: updates.map(item => ({
        operation: 'update',
        id: item.id,
        updates: item.updates,
      })),
    });
    return result.fieldResults;
  }

  async reorderFields(templateId: string, updates: FieldUpdate[]): Promise<boolean[]> {
    this.assertAllowed('field', 'reorder');
    if (updates.length === 0) return [];

    const result = await this.database.applyChecklistStructuralMutation({
      templateId,
      fieldMutations: updates.map(item => ({
        operation: 'update',
        id: item.id,
        updates: item.updates,
      })),
    });
    return result.fieldResults;
  }

  async retireTemplate(templateId: string, retiredAt: string = this.now().toISOString()): Promise<boolean> {
    this.assertAllowed('template', 'retire');
    return this.database.retireItemAndPending(
      tables.TEMPLATES_CHECKLIST.NAME,
      templateId,
      { retired_at: retiredAt },
      { retired_at: retiredAt }
    );
  }

  async retireField(templateId: string, fieldId: string, retiredAt: string = this.now().toISOString()): Promise<boolean> {
    this.assertAllowed('field', 'retire');
    const result = await this.database.applyChecklistStructuralMutation({
      templateId,
      fieldMutations: [{
        operation: 'delete',
        id: fieldId,
        updates: { retired_at: retiredAt },
        pendingData: { retired_at: retiredAt },
      }],
    });
    return result.fieldResults[0] ?? false;
  }

  public assertAllowed(
    entity: StructuralEntity,
    action: StructuralAction
  ): void {
    const gate = assertStructuralOnline(entity, action, this.isOnline());
    if (!gate.allowed) {
      throw new ChecklistStructuralMutationBlockedError(gate);
    }
  }

}

export const checklistStructuralMutationService = new ChecklistStructuralMutationService();
